import React, { useState, useEffect } from 'react';
import { RootState } from '../Redux/store';
import { useDispatch, useSelector } from 'react-redux';
import { setAuth } from '../Redux/authSlice';
import { translations } from '../utils/translations';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Modal, Button, Card, Breadcrumb, Pagination, Table } from 'react-bootstrap';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import "./expense.css";
interface Expense {
    id: number;
    Expenses_type: string;
    Cost: string;
    date_paid_expenses: string;
}

export default function ExpenseReport() {
    const [expenses, setExpenses] = useState<Expense[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [userRole, setUserRole] = useState<string | null>(null);
    const [showEditModal, setShowEditModal] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [searchTerm, setSearchTerm] = useState<string>('');

    const dispatch = useDispatch();
    const auth = useSelector((state: RootState) => state.auth.value);
    const language = useSelector((state: RootState) => state.language.language);
    const t = translations[language as keyof typeof translations];

    const getCookie = (name: string) => {
        const cookieName = name + "=";
        const decodedCookie = decodeURIComponent(document.cookie);
        const cookieArray = decodedCookie.split(';');
        for (let i = 0; i < cookieArray.length; i++) {
            let cookie = cookieArray[i];
            while (cookie.charAt(0) === ' ') {
                cookie = cookie.substring(1);
            }
            if (cookie.indexOf(cookieName) === 0) {
                return cookie.substring(cookieName.length, cookie.length);
            }
        }
        return '';
    };

    const fetchData = async () => {
        try {
            const token = getCookie('token');
            if (!token) {
                dispatch(setAuth(false));
                return;
            }
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            const userResponse = await axios.get('https://api.sominnovations.xyz/users/user', {
                headers: { 'Content-Type': 'application/json' },
            });
            setUserRole(userResponse.data.user.role);
            dispatch(setAuth(true));
        } catch (e) {
            dispatch(setAuth(false));
        }
    };

    const fetchExpenses = async (page: number) => {
        setLoading(true);
        try {
            const response = await axios.get(`https://api.sominnovations.xyz/Branches/expenses/?page=${page}&search=${searchTerm}`, {
                headers: { 'Content-Type': 'application/json' },
            });
            setExpenses(response.data.results);
            setTotalPages(Math.ceil(response.data.count / 20));
        } catch (error) {
            console.error('Error fetching expenses:', error);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchData();
    }, [dispatch]);

    useEffect(() => {
        if (auth) {
            fetchExpenses(currentPage);
        }
    }, [auth, currentPage, searchTerm]);

    const downloadPDF = () => {
        const doc = new jsPDF();
        doc.text('Expenses Report', 14, 16);
        doc.autoTable({
            head: [['Expense Type', 'Cost', 'Date Paid']],
            body: expenses.map(expense => [expense.Expenses_type, expense.Cost, expense.date_paid_expenses]),
        });
        doc.save('expenses_report.pdf');
    };

    const downloadCSV = () => {
        const csvData = expenses.map(expense => ({
            'Expense Type': expense.Expenses_type,
            'Cost': expense.Cost,
            'Date Paid': expense.date_paid_expenses,
        }));
        const csv = Papa.unparse(csvData);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'expenses_report.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const downloadExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(expenses.map(expense => ({
            'Expense Type': expense.Expenses_type,
            'Cost': expense.Cost,
            'Date Paid': expense.date_paid_expenses,
        })));
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Expenses');
        XLSX.writeFile(workbook, 'expenses_report.xlsx');
    };

    return (
        <div className="container mt-1 expenses-container">
            <Card className="mb-4">
                <Card.Header style={{ backgroundColor: '#ffcc00', color: '#fff' }}>
                    <div className="d-flex justify-content-between align-items-center">
                        <h4>{t.expenses}</h4>
                        <Breadcrumb>
                            <Breadcrumb.Item href="dashboard">{t.dashboard}</Breadcrumb.Item>
                            <Breadcrumb.Item active>{t.expenses}</Breadcrumb.Item>
                        </Breadcrumb>
                    </div>
                </Card.Header>
                <Card.Body>
                    <Button variant="info" className="ms-2" onClick={downloadPDF}>{t.downloadPDF}</Button>
                    <Button variant="success" className="ms-2" onClick={downloadCSV}>{t.downloadCSV}</Button>
                    <Button variant="primary" className="ms-2" onClick={downloadExcel}>{t.downloadExcel}</Button>
                    {loading ? (
                        <div className="text-center">Loading...</div>
                    ) : (
                        <Table striped bordered hover style={{ margin: "10px" }}>
                            <thead>
                                <tr>
                                    <th>{t.expenseType}</th>
                                    <th>{t.expenseCost}</th>
                                    <th>{t.datePaid}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {expenses.map(expense => (
                                    <tr key={expense.id}>
                                        <td>{expense.Expenses_type}</td>
                                        <td>{expense.Cost}</td>
                                        <td>{expense.date_paid_expenses}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                    <Pagination>
                        <Pagination.Prev onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} />
                        {Array.from({ length: totalPages }, (_, index) => (
                            <Pagination.Item key={index} active={index + 1 === currentPage} onClick={() => setCurrentPage(index + 1)}>
                                {index + 1}
                            </Pagination.Item>
                        ))}
                        <Pagination.Next onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages} />
                    </Pagination>
                </Card.Body>
            </Card>
        </div>
    );
}
